/**
 * Every active filter is visible and removable in one click.
 *
 * The rail and the header controls each show only their own state, so with more
 * than one filter applied there is no single place that answers "why am I
 * seeing this few results?". That is what this row is for — and it is why a chip
 * exists for filters set from the header too, not just from the rail.
 *
 * `chipsFor` is exported on its own (same shape as Universities' `chipsFor` in
 * UniversitiesPage.jsx) so the page can ask "is anything actually filtered?"
 * without duplicating this list of every filter key a second time — ProgramsPage
 * uses its `.length` to decide whether the result count means anything yet.
 */
export function chipsFor(filters, options, dispatch) {
  const chips = [];
  const add = (label, clear) => chips.push({ label, clear });

  if (filters.q) {
    add(`"${filters.q}"`, () => dispatch({ type: 'SET_QUERY', value: '' }));
  }
  for (const c of filters.countries) {
    add(c, () => dispatch({ type: 'SET_COUNTRY', value: '' }));
  }
  if (filters.nationality) {
    const label = (options?.nationalities ?? []).find((n) => n.id === filters.nationality)?.name ?? filters.nationality;
    add(`Nationality: ${label}`, () => dispatch({ type: 'SET_NATIONALITY', value: '' }));
  }
  // Course type is chosen by NAME now (see degreeLevels in
  // useProgramSearch.js), so the chip needs no id->name lookup — the filter
  // value already is the label.
  for (const name of filters.degreeLevels ?? []) {
    add(name, () => dispatch({ type: 'TOGGLE', key: 'degreeLevels', value: name }));
  }
  // Exact intake is still chosen by id; the chip has to say the name. Falling
  // back to the id keeps the chip clearable if the option list has not loaded
  // yet, which is better than rendering nothing removable.
  const nameOf = (list, id) => (list ?? []).find((o) => o.id === id)?.name ?? id;
  for (const id of filters.intakeIds ?? []) {
    add(`Intake: ${nameOf(options?.intakeOptions, id)}`,
      () => dispatch({ type: 'TOGGLE', key: 'intakeIds', value: id }));
  }
  // The only reachable value from the UI is 'active' — the toggle has no
  // 'inactive' state (see SearchBar's Active status control).
  if (filters.intakeStatus === 'active') {
    add('Active status: On', () => dispatch({ type: 'SET_ACTIVE_STATUS', value: false }));
  }
  for (const u of filters.universities) {
    add(u, () => dispatch({ type: 'TOGGLE', key: 'universities', value: u }));
  }
  for (const t of filters.englishTests) {
    add(`English: ${t}`, () => dispatch({ type: 'TOGGLE', key: 'englishTests', value: t }));
  }
  if (filters.tuitionMin != null || filters.tuitionMax != null) {
    const lo = (filters.tuitionMin ?? 0).toLocaleString();
    const hi = filters.tuitionMax == null ? '∞' : filters.tuitionMax.toLocaleString();
    add(`£${lo} – £${hi}`, () => dispatch({ type: 'SET_TUITION', min: null, max: null }));
  }
  for (const label of filters.durationLabels ?? []) {
    add(`Duration: ${label}`,
      () => dispatch({ type: 'TOGGLE', key: 'durationLabels', value: label }));
  }
  if (filters.scholarship) {
    add('Scholarship', () => dispatch({ type: 'SET_FLAG', key: 'scholarship', value: false }));
  }
  if (filters.freeToApply) {
    add('No application fee', () => dispatch({ type: 'SET_FLAG', key: 'freeToApply', value: false }));
  }

  return chips;
}

export function FilterChips({ filters, options, dispatch }) {
  const chips = chipsFor(filters, options, dispatch);

  // Nothing filtered — render nothing, so the results start right under the
  // filter card instead of after a "No filters applied" spacer line.
  if (chips.length === 0) return null;

  return (
    <div className="chips" id="chips">
      <span className="lab">Active:</span>
      {chips.map((c) => (
        <span className="chip" key={c.label} onClick={c.clear}>
          {c.label}<b>×</b>
        </span>
      ))}
      <button type="button" className="clear" onClick={() => dispatch({ type: 'CLEAR_ALL' })}>
        Clear All
      </button>
    </div>
  );
}
